import { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, Search, Trash2, Pencil, LogIn, Users, DownloadCloud } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const emptyForm = {
  halo_user_ref: "", halo_user_name: "", halo_user_email: "",
  cpanel_username: "", whm_server_id: "", notes: "",
};

export default function MappingsPage() {
  const [rows, setRows] = useState([]);
  const [servers, setServers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [shadowLoading, setShadowLoading] = useState({});
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncServerId, setSyncServerId] = useState("");
  const [syncOverwrite, setSyncOverwrite] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, s] = await Promise.all([
        api.get("/mappings", { params: q ? { q } : {} }),
        api.get("/servers"),
      ]);
      setRows(data);
      setServers(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      halo_user_ref: r.halo_user_ref, halo_user_name: r.halo_user_name || "",
      halo_user_email: r.halo_user_email || "", cpanel_username: r.cpanel_username,
      whm_server_id: r.whm_server_id || "", notes: r.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.whm_server_id) return toast.error("Pick a WHM server");
    setSaving(true);
    try {
      if (editing) {
        const { halo_user_ref, ...rest } = form; // ref not editable
        await api.patch(`/mappings/${editing.id}`, rest);
        toast.success("Mapping updated");
      } else {
        await api.post("/mappings", form);
        toast.success("Mapping created");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/mappings/${confirmDel.id}`);
      toast.success("Mapping deleted");
      setConfirmDel(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shadow = async (r) => {
    setShadowLoading((s) => ({ ...s, [r.id]: true }));
    try {
      const { data } = await api.post(`/mappings/${r.id}/shadow-login`);
      if (data.ok && data.url) {
        toast.success(`Opening cPanel for ${r.cpanel_username}…`);
        window.open(data.url, "_blank", "noopener");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShadowLoading((s) => ({ ...s, [r.id]: false })); }
  };

  const openSync = () => {
    setSyncResult(null);
    setSyncOverwrite(false);
    setSyncServerId(servers[0]?.id || "");
    setSyncOpen(true);
  };

  const runSync = async () => {
    if (!syncServerId) return toast.error("Pick a WHM server");
    setSyncBusy(true);
    try {
      const { data } = await api.post("/mappings/sync-halo", {
        default_whm_server_id: syncServerId,
        overwrite_existing: syncOverwrite,
      });
      setSyncResult(data);
      toast.success(`Sync complete: ${data.stats.created} created, ${data.stats.updated} updated`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="User Mappings"
        description="Halo user reference ↔ cPanel username ↔ WHM server. Used by the /sso/cpanel bridge and the helpdesk shadow-login."
        testId="mappings-header"
        actions={
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="w-56 pl-8"
                placeholder="Search users…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="mappings-search-input"
              />
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid="mappings-refresh-btn">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={openSync} disabled={!servers.length} data-testid="mappings-sync-halo-btn">
              <DownloadCloud className="mr-1.5 h-4 w-4" /> Sync from Halo
            </Button>
            <Button size="sm" onClick={openNew} disabled={!servers.length} data-testid="mappings-add-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add mapping
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No mappings yet"
          description={servers.length === 0
            ? "Add a WHM server first — mappings must point at one."
            : "Create a mapping to link a Halo user with a cPanel account on a WHM server."}
          action={servers.length > 0 ? (
            <Button size="sm" onClick={openNew} data-testid="empty-add-mapping-btn"><Plus className="mr-1.5 h-4 w-4" /> Add mapping</Button>
          ) : null}
          testId="mappings-empty"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-2.5">Halo user</th>
                <th className="px-4 py-2.5">Halo ref</th>
                <th className="px-4 py-2.5">cPanel user</th>
                <th className="px-4 py-2.5">WHM server</th>
                <th className="px-4 py-2.5">Last SSO</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40" data-testid={`mapping-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.halo_user_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{r.halo_user_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.halo_user_ref}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.cpanel_username}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.whm_server_label}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{r.whm_server_hostname}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.last_sso_at ? new Date(r.last_sso_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => shadow(r)} disabled={shadowLoading[r.id]} data-testid={`shadow-login-btn-${r.id}`}>
                        <LogIn className={`mr-1.5 h-3.5 w-3.5 ${shadowLoading[r.id] ? "animate-pulse" : ""}`} />
                        Shadow login
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-mapping-btn-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(r)} data-testid={`delete-mapping-btn-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="mapping-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit mapping" : "Add mapping"}</DialogTitle>
            <DialogDescription>Halo user reference is what your custom button will send as `halo_user_ref`.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Halo user reference</Label>
              <Input
                value={form.halo_user_ref}
                onChange={(e) => setForm({ ...form, halo_user_ref: e.target.value })}
                required disabled={!!editing}
                placeholder="1234 (Halo user ID)"
                data-testid="mapping-haloref-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Halo display name</Label>
                <Input value={form.halo_user_name} onChange={(e) => setForm({ ...form, halo_user_name: e.target.value })}
                  data-testid="mapping-haloname-input" />
              </div>
              <div>
                <Label>Halo email</Label>
                <Input type="email" value={form.halo_user_email}
                  onChange={(e) => setForm({ ...form, halo_user_email: e.target.value })}
                  data-testid="mapping-haloemail-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>cPanel username</Label>
                <Input value={form.cpanel_username}
                  onChange={(e) => setForm({ ...form, cpanel_username: e.target.value })}
                  required data-testid="mapping-cpuser-input" />
              </div>
              <div>
                <Label>WHM server</Label>
                <Select value={form.whm_server_id} onValueChange={(v) => setForm({ ...form, whm_server_id: v })}>
                  <SelectTrigger data-testid="mapping-server-select">
                    <SelectValue placeholder="Choose server" />
                  </SelectTrigger>
                  <SelectContent>
                    {servers.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`mapping-server-opt-${s.id}`}>
                        {s.label} · {s.hostname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="mapping-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="mapping-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="mapping-save-btn">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sync-halo-dialog">
          <DialogHeader>
            <DialogTitle>Sync users from HaloPSA</DialogTitle>
            <DialogDescription>
              Pulls every Halo user who has a value in the configured cPanel field and
              creates a mapping row for them. Users already mapped are skipped by default.
            </DialogDescription>
          </DialogHeader>

          {!syncResult ? (
            <div className="space-y-3">
              <div>
                <Label>Default WHM server for new mappings</Label>
                <Select value={syncServerId} onValueChange={setSyncServerId}>
                  <SelectTrigger data-testid="sync-server-select"><SelectValue placeholder="Choose server" /></SelectTrigger>
                  <SelectContent>
                    {servers.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`sync-server-opt-${s.id}`}>
                        {s.label} · {s.hostname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Existing rows keep their assigned server.</p>
              </div>
              <label className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                <input
                  type="checkbox" className="mt-0.5"
                  checked={syncOverwrite}
                  onChange={(e) => setSyncOverwrite(e.target.checked)}
                  data-testid="sync-overwrite-checkbox"
                />
                <span>
                  <strong>Overwrite existing cPanel usernames.</strong> If Halo&apos;s value differs from
                  what&apos;s stored, update the row instead of skipping it.
                </span>
              </label>
            </div>
          ) : (
            <div className="space-y-2 text-sm" data-testid="sync-result">
              <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div>Fetched from Halo</div><div className="text-right font-mono">{syncResult.stats.total_fetched}</div>
                  <div className="text-emerald-600 dark:text-emerald-400">Created</div><div className="text-right font-mono">{syncResult.stats.created}</div>
                  <div className="text-blue-600 dark:text-blue-400">Updated</div><div className="text-right font-mono">{syncResult.stats.updated}</div>
                  <div className="text-muted-foreground">Skipped (already mapped)</div><div className="text-right font-mono">{syncResult.stats.skipped_existing}</div>
                  <div className="text-muted-foreground">Skipped (no cPanel field)</div><div className="text-right font-mono">{syncResult.stats.skipped_no_cpanel}</div>
                  <div className={syncResult.stats.errors ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}>Errors</div>
                  <div className="text-right font-mono">{syncResult.stats.errors}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Cpanel field read: <code className="font-mono">{syncResult.cpanel_field_name}</code> · Server used for new rows: <strong>{syncResult.whm_server_label}</strong>
              </p>
              {syncResult.error_samples?.length ? (
                <div className="max-h-32 overflow-y-auto rounded-md border border-red-500/30 bg-red-500/5 p-2 text-xs">
                  {syncResult.error_samples.map((er, i) => (
                    <div key={i} className="font-mono text-[11px]">#{er.halo_ref}: {er.error}</div>
                  ))}
                </div>
              ) : null}
              {syncResult.debug_sample && syncResult.stats.created === 0 && syncResult.stats.updated === 0 ? (
                <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs" data-testid="sync-debug-sample">
                  <div className="mb-2 font-semibold text-amber-800 dark:text-amber-300">
                    Nothing matched — diagnostic scan of all {syncResult.stats.total_fetched} Halo users:
                  </div>
                  <div className="mb-1">
                    Searched field: <code className="font-mono">{syncResult.debug_sample.searched_field_name}</code>
                  </div>
                  {syncResult.debug_sample.cpanel_like_top_level_keys?.length ? (
                    <div className="mb-2 rounded border border-emerald-500/40 bg-emerald-500/5 p-2">
                      <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                        Found top-level cPanel-like key(s):
                      </div>
                      <code className="font-mono">
                        {syncResult.debug_sample.cpanel_like_top_level_keys.join(", ")}
                      </code>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        → Set Settings → HaloPSA → cPanel username field name to one of these and re-sync.
                      </div>
                    </div>
                  ) : null}
                  {syncResult.debug_sample.cpanel_like_customfield_labels?.length ? (
                    <div className="mb-2 rounded border border-emerald-500/40 bg-emerald-500/5 p-2">
                      <div className="font-semibold text-emerald-700 dark:text-emerald-300">
                        Found custom field label(s) in `customfields` array:
                      </div>
                      <code className="font-mono">
                        {syncResult.debug_sample.cpanel_like_customfield_labels.join(", ")}
                      </code>
                    </div>
                  ) : null}
                  {syncResult.debug_sample.populated_question_slots
                    && Object.keys(syncResult.debug_sample.populated_question_slots).length ? (
                    <div className="mb-2 rounded border border-blue-500/40 bg-blue-500/5 p-2">
                      <div className="font-semibold text-blue-700 dark:text-blue-300">
                        Populated `questionN` slots (Halo&apos;s legacy user custom-field storage):
                      </div>
                      <pre className="mt-1 whitespace-pre-wrap text-[11px] font-mono">
                        {Object.entries(syncResult.debug_sample.populated_question_slots)
                          .map(([k, v]) => `${k}: ${v} user(s)`)
                          .join("\n")}
                      </pre>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        → Check in Halo <em>Configuration → Custom Fields → Users</em> which slot is labelled
                        &quot;cpanelusername&quot;. Then set the bridge Settings → HaloPSA → cPanel username field name
                        to that exact slot name (e.g. <code className="font-mono">question3</code>).
                      </div>
                    </div>
                  ) : null}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-muted-foreground">
                      List-endpoint top-level keys ({syncResult.debug_sample.top_level_keys?.length || 0})
                    </summary>
                    <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all text-[10px] font-mono">
                      {(syncResult.debug_sample.top_level_keys || []).join(", ")}
                    </pre>
                  </details>
                  {syncResult.debug_sample.first_user_detail ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-muted-foreground">
                        First user single-record detail keys ({syncResult.debug_sample.first_user_detail.top_level_keys?.length || 0})
                      </summary>
                      <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all text-[10px] font-mono">
                        {(syncResult.debug_sample.first_user_detail.top_level_keys || []).join(", ")}
                      </pre>
                      {syncResult.debug_sample.first_user_detail.customfields_shape_first_20 ? (
                        <div className="mt-2">
                          <div className="text-[11px] font-semibold">customfields array (values redacted):</div>
                          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-[10px] font-mono">
                            {JSON.stringify(syncResult.debug_sample.first_user_detail.customfields_shape_first_20, null, 2)}
                          </pre>
                        </div>
                      ) : null}
                    </details>
                  ) : syncResult.debug_sample.first_user_detail_diag ? (
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Single-record detail fetch: <code className="font-mono">{syncResult.debug_sample.first_user_detail_diag}</code>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter>
            {syncResult ? (
              <Button onClick={() => setSyncOpen(false)} data-testid="sync-close-btn">Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={syncBusy} data-testid="sync-cancel-btn">Cancel</Button>
                <Button onClick={runSync} disabled={syncBusy || !syncServerId} data-testid="sync-run-btn">
                  <DownloadCloud className={`mr-1.5 h-4 w-4 ${syncBusy ? "animate-pulse" : ""}`} />
                  {syncBusy ? "Syncing…" : "Run sync"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              The Halo user will fall back to the provisioning-ticket flow on next SSO attempt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mapping-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="mapping-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
